import { useState } from "react";
import api from "@/lib/api";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { fmtDate } from "../serviceApi";

const url = (id) => (id ? `/service/recurring/schedules/${id}` : "/service/recurring/schedules");

// Validates a schedule (dry run). If its reminder date already passed, asks: send now, skip this occurrence, or change the date.
// Resolves to { late_action } to proceed, or null when the user wants to change the date.
export function useScheduleSaver() {
  const [late, setLate] = useState(null);

  const check = async (body, id) => {
    try {
      await (id ? api.put : api.post)(url(id), { ...body, dry_run: true });
      return { late_action: null };
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status !== 409 || d?.code !== "late_reminder") throw e;
      const choice = await new Promise((resolve) => setLate({ ...d, resolve }));
      setLate(null);
      return choice === "change" ? null : { late_action: choice };
    }
  };
  const save = (body, id) => (id ? api.put : api.post)(url(id), body).then((r) => r.data);

  const pick = (v) => late?.resolve(v);
  const dialog = (
    <AlertDialog open={!!late} onOpenChange={(v) => !v && pick("change")}>
      <AlertDialogContent data-testid="recurring-late-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>The reminder date has already passed</AlertDialogTitle>
          <AlertDialogDescription>
            With this lead time the reminder should have gone out on {fmtDate(late?.reminder_date)}. Nothing is sent automatically — choose what to do for this occurrence.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-2">
          <AlertDialogCancel onClick={() => pick("change")} data-testid="recurring-late-change">Change due date</AlertDialogCancel>
          <Button variant="outline" onClick={() => pick("skip")} data-testid="recurring-late-skip">Skip this reminder</Button>
          <Button onClick={() => pick("send_now")} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="recurring-late-send-now">Send now</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return { check, save, dialog };
}
